import { useEffect, useId, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link, useLocation } from 'react-router-dom'
import { useDisplayStore } from '../../context/DisplayStoreProvider'
import { useWalletAuth } from '../../context/WalletAuthProvider'
import { MORE_LINKS, TOOL_LINKS } from '../../lib/navLinks'

const PAGE_LINKS = [
  { testId: 'navbar-trade-link', href: '/trade/LIT', label: 'Trade', isActive: (path) => path.startsWith('/trade') },
  { testId: 'navbar-portfolio-link', href: '/portfolio', label: 'Portfolio', isActive: (path) => path.startsWith('/portfolio') },
  { testId: 'navbar-public-pools-link', href: '/public-pools', label: 'Public Pools', isActive: (path) => path.startsWith('/public-pools') },
  { testId: 'navbar-staking-link', href: '/staking', label: 'Staking', isActive: (path) => path.startsWith('/staking') },
  { testId: 'navbar-referrals-link', href: '/referrals', label: 'Referrals', isActive: (path) => path.startsWith('/referrals') },
]

const LINK_IDLE =
  'flex items-center gap-2.5 rounded-card px-3 py-2 text-left text-sm transition-colors text-ink/80 hover:bg-ink/5 hover:text-ink'
const LINK_ACTIVE =
  'flex items-center gap-2.5 rounded-card px-3 py-2 text-left text-sm transition-colors bg-ink/6 font-medium text-ink'
const SECTION_TRIGGER =
  'px-3 pb-1 text-2xs font-medium tracking-caps text-muted uppercase flex w-full cursor-pointer items-center justify-between pt-1 transition-colors hover:text-ink'

function CloseIcon() {
  return (
    <span data-icon="true" className="flex size-3.5 shrink-0 items-center justify-center">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" fill="currentColor" className="size-full">
        <path d="M205.66,194.34a8,8,0,0,1-11.32,11.32L128,139.31,61.66,205.66a8,8,0,0,1-11.32-11.32L116.69,128,50.34,61.66A8,8,0,0,1,61.66,50.34L128,116.69l66.34-66.35a8,8,0,0,1,11.32,11.32L139.31,128Z" />
      </svg>
    </span>
  )
}

function ChevronIcon({ open }) {
  return (
    <span
      data-icon="true"
      className={`flex size-3 shrink-0 items-center justify-center text-ink/50 transition-transform duration-200${open ? ' rotate-180' : ''}`}
    >
      <svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" className="size-full">
        <path
          d="M3 4.75 6 7.5l3-2.75"
          stroke="currentColor"
          strokeWidth="1.25"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  )
}

function StatusPip() {
  return (
    <span data-icon="true" className="flex h-2 w-0.5 shrink-0 items-center justify-center text-current">
      <svg viewBox="0 0 2 8" xmlns="http://www.w3.org/2000/svg" fill="none" className="size-full">
        <rect width="2" height="8" rx="1" fill="currentColor" />
      </svg>
    </span>
  )
}

function SidebarLink({ item, pathname, onNavigate }) {
  const active = item.isActive?.(pathname)
  const className = active ? LINK_ACTIVE : LINK_IDLE

  if (item.type === 'button') {
    return (
      <button type="button" className={className} data-testid={item.testId} onClick={onNavigate}>
        {item.label}
      </button>
    )
  }

  if (item.external) {
    return (
      <a
        data-testid={item.testId}
        className={className}
        href={item.href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onNavigate}
      >
        {item.label}
      </a>
    )
  }

  return (
    <Link
      data-testid={item.testId}
      aria-current={active ? 'page' : undefined}
      className={className}
      to={item.href}
      data-discover="true"
      onClick={onNavigate}
    >
      {item.label}
    </Link>
  )
}

function CollapsibleSection({ title, open, onToggle, contentId, children }) {
  return (
    <div data-state={open ? 'open' : 'closed'}>
      <button
        type="button"
        className={SECTION_TRIGGER}
        aria-controls={contentId}
        aria-expanded={open}
        data-state={open ? 'open' : 'closed'}
        onClick={onToggle}
      >
        {title}
        <ChevronIcon open={open} />
      </button>
      <div
        data-state={open ? 'open' : 'closed'}
        id={contentId}
        hidden={!open}
        className="flex flex-col gap-0.5 overflow-hidden data-[state=closed]:animate-collapsible-slide-up data-[state=open]:animate-collapsible-slide-down"
      >
        {children}
      </div>
    </div>
  )
}

function truncateAddress(address) {
  if (!address) return ''
  return `${address.slice(0, 6)}...${address.slice(-4)}`.toUpperCase()
}

export default function MobileSidebar({ open, onClose, blockHeight }) {
  const { pathname } = useLocation()
  const { openConnectModal } = useDisplayStore()
  const { isConnected, userAddress } = useWalletAuth()
  const titleId = useId()
  const toolsId = useId()
  const moreId = useId()
  const [toolsOpen, setToolsOpen] = useState(true)
  const [moreOpen, setMoreOpen] = useState(true)
  const [rendered, setRendered] = useState(open)
  const [drawerState, setDrawerState] = useState(open ? 'open' : 'closed')

  useEffect(() => {
    if (open) {
      setRendered(true)
      const frame = requestAnimationFrame(() => setDrawerState('open'))
      return () => cancelAnimationFrame(frame)
    }
    setDrawerState('closed')
    const timeout = setTimeout(() => setRendered(false), 500)
    return () => clearTimeout(timeout)
  }, [open])

  useEffect(() => {
    if (!open) return undefined
    const onKey = (event) => {
      if (event.key === 'Escape') onClose()
    }
    const onResize = () => {
      if (window.innerWidth >= 768) onClose()
    }
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    window.addEventListener('keydown', onKey)
    window.addEventListener('resize', onResize)
    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('resize', onResize)
    }
  }, [open, onClose])

  if (!rendered) return null

  const handleNavigate = () => onClose()
  const handleWalletStatus = () => {
    if (!isConnected) {
      onClose()
      openConnectModal()
    }
  }

  return createPortal(
    <>
      <div
        data-vaul-overlay=""
        data-vaul-snap-points="false"
        data-state={drawerState}
        className="fixed inset-0 z-50 bg-black/60"
        style={{ pointerEvents: drawerState === 'open' ? 'auto' : 'none' }}
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-labelledby={titleId}
        data-state={drawerState}
        data-vaul-drawer-direction="right"
        data-vaul-drawer=""
        data-vaul-delayed-snap-points="false"
        data-vaul-snap-points="false"
        data-vaul-custom-container="false"
        data-vaul-animate="true"
        className="fixed inset-y-0 right-0 z-50 flex w-[280px] max-w-[85vw] flex-col border-l-[0.5px] border-line bg-overlay text-ink shadow-elevate-modal outline-none"
        data-testid="mobile-sidebar"
        tabIndex={-1}
        style={{ pointerEvents: 'auto' }}
      >
        <div className="flex h-[52px] shrink-0 items-center justify-between border-b-[0.5px] border-line px-4">
          <h2 id={titleId} className="text-base font-medium text-ink">
            Menu
          </h2>
          <button
            type="button"
            className="inline-flex size-7 items-center justify-center rounded-pill text-ink/60 transition-colors hover:bg-ink/5 hover:text-ink"
            aria-label="Close navigation menu"
            onClick={onClose}
          >
            <CloseIcon />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
          <nav className="flex flex-col gap-0.5 p-2">
            <p className="px-3 pb-1 text-2xs font-medium tracking-caps text-muted uppercase">Pages</p>
            {PAGE_LINKS.map((item) => (
              <SidebarLink key={item.testId} item={item} pathname={pathname} onNavigate={handleNavigate} />
            ))}
          </nav>

          <nav className="flex flex-col gap-0.5 border-t-[0.5px] border-line p-2">
            <CollapsibleSection
              title="Tools"
              open={toolsOpen}
              onToggle={() => setToolsOpen((value) => !value)}
              contentId={toolsId}
            >
              {TOOL_LINKS.map((item) => (
                <SidebarLink key={item.testId} item={item} pathname={pathname} onNavigate={handleNavigate} />
              ))}
            </CollapsibleSection>
          </nav>

          <nav className="flex flex-col gap-0.5 border-t-[0.5px] border-line p-2">
            <CollapsibleSection
              title="More"
              open={moreOpen}
              onToggle={() => setMoreOpen((value) => !value)}
              contentId={moreId}
            >
              {MORE_LINKS.map((item) => (
                <SidebarLink key={item.testId} item={item} pathname={pathname} onNavigate={handleNavigate} />
              ))}
            </CollapsibleSection>
          </nav>
        </div>

        <div className="flex shrink-0 flex-col gap-2.5 border-t-[0.5px] border-line bg-fill px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] font-mono">
          <div className="flex flex-col items-start gap-1.5 label-meta">
            <a href="https://status.lighter.xyz" target="_blank" rel="noopener noreferrer">
              <span className="flex items-center gap-1 text-up-strong" data-testid="sidebar-server-status">
                <StatusPip />
                ONLINE / MAINNET
              </span>
            </a>
            <button
              type="button"
              className={`flex items-center gap-1 ${isConnected ? 'text-up-strong' : 'text-down'} cursor-pointer hover:opacity-80`}
              data-testid="sidebar-wallet-status"
              onClick={handleWalletStatus}
            >
              <StatusPip />
              {isConnected ? truncateAddress(userAddress) : 'WALLET NOT CONNECTED'}
            </button>
            <span className="flex items-center gap-1 text-up-strong" data-testid="sidebar-ws-status">
              <StatusPip />
              WS CONNECTED
            </span>
          </div>
          <div className="flex flex-col items-start gap-1.5 label-meta text-meta">
            <a href="https://docs.lighter.xyz" target="_blank" rel="noopener noreferrer" className="transition-colors hover:text-ink">
              Docs
            </a>
            <a href="https://lighter.xyz/terms" target="_blank" rel="noopener noreferrer" className="transition-colors hover:text-ink">
              Terms
            </a>
            <a href="https://lighter.xyz/privacy" target="_blank" rel="noopener noreferrer" className="transition-colors hover:text-ink">
              Privacy
            </a>
          </div>
          <div className="flex items-center justify-between gap-2">
            <button type="button" className="flex items-center gap-1.5">
              <span className="h-3.5 w-1 rounded-seam bg-link" />
              <span className="label-meta text-ink/80">Block</span>
              <span className="label-meta text-link tabular-nums">{blockHeight}</span>
            </button>
            <div className="flex items-center gap-2.5">
              <Link
                className="label-meta text-meta transition-colors hover:text-ink"
                data-testid="sidebar-version-text"
                to="/trade"
                data-discover="true"
                onClick={onClose}
              >
                v6.0.26
              </Link>
              <span className="label-meta text-faint">8/24/2026</span>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  )
}
